import { format, isSameDay, isToday, isYesterday } from 'date-fns';

import type { SupportMessageStatus } from './SupportMessageBubble';

import { toInquiryForward, type InquiryForward } from '@/services/conversations/inquiryForward';
import {
  toReferencedInquiry,
  type ReferencedInquiry,
} from '@/services/conversations/referencedInquiry';
import { toSharedSurvey, type SharedSurvey } from '@/services/conversations/sharedSurvey';
import type {
  MessageReference,
  OutgoingFile,
  StoredMessageFile,
  SupportThread,
} from '@/services/conversations/thread';

/**
 * A file as a bubble shows it. A stored file has `s3Key` and is fetched by it;
 * a pending one shows from `uri` on this device until its stored copy arrives.
 */
export type ThreadFile = {
  key: string;
  name: string;
  mimeType: string;
  s3Key?: string;
  uri?: string;
};

export type ThreadMessageItem = {
  kind: 'message';
  key: string;
  id: string;
  body: string;
  createdAt: string;
  isOwn: boolean;
  status: SupportMessageStatus;
  referenceConversationId: string | null;
  inquiryForward: InquiryForward | null;
  /** The linked inquiry's details for its card; null on messages stored before they were kept. */
  referencedInquiry: ReferencedInquiry | null;
  /** The survey the message shares, for its card. */
  sharedSurvey: SharedSurvey | null;
  files: ThreadFile[];
  /** The inquiry a local message was sent with, so a retry sends it again. */
  reference?: MessageReference;
  /** The survey a local message was sent with, so a retry sends it again. */
  survey?: SharedSurvey;
  /** The files a local message was sent with, so a retry sends them again. */
  outgoingFiles?: OutgoingFile[];
  /** Why the server refused a failed message, when it named a reason. */
  failureCode?: string;
};

export type ThreadItem = { kind: 'day'; key: string; label: string } | ThreadMessageItem;

/** A stored message's files, skipping any entry not shaped like one, since attributes are free-form. */
function storedFiles(files: StoredMessageFile[] | undefined): ThreadFile[] {
  if (!Array.isArray(files)) {
    return [];
  }

  return files
    .filter(
      (file) =>
        typeof file?.s3Key === 'string' &&
        typeof file.fileName === 'string' &&
        typeof file.mimeType === 'string'
    )
    .map((file) => ({
      key: file.s3Key,
      name: file.fileName,
      mimeType: file.mimeType,
      s3Key: file.s3Key,
    }));
}

function dayLabel(date: Date): string {
  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  return format(date, 'EEEE, MMM d');
}

/**
 * The thread as rows, oldest first: stored messages in index order, then this
 * device's pending and failed sends, with a day label wherever the day
 * changes. A local message is always the reader's own.
 *
 * Ownership is the author id against the viewer's own, so staff acting as
 * Primum Support see every support-authored message, whichever colleague
 * sent it, on their own side.
 */
export function buildThreadItems(
  thread: SupportThread | undefined,
  viewerId: string | undefined
): ThreadItem[] {
  if (!thread) {
    return [];
  }

  const messages: ThreadMessageItem[] = [
    ...thread.messages.map((message) => ({
      kind: 'message' as const,
      key: message.id,
      id: message.id,
      body: message.body,
      createdAt: message.createdAt,
      isOwn: String(message.authorId) === viewerId,
      status: 'sent' as const,
      referenceConversationId: message.referenceConversationId,
      inquiryForward: toInquiryForward(message.attributes?.inquiryForward),
      referencedInquiry: toReferencedInquiry(message.attributes?.referenceInquiry),
      sharedSurvey: toSharedSurvey(message.attributes?.sharedSurvey),
      files: storedFiles(message.attributes?.files),
    })),
    ...thread.local.map((message) => ({
      kind: 'message' as const,
      key: message.id,
      id: message.id,
      body: message.body,
      createdAt: message.createdAt,
      isOwn: true,
      status: message.status,
      referenceConversationId: message.reference?.conversationId ?? null,
      inquiryForward: null,
      referencedInquiry: message.reference?.details ?? null,
      reference: message.reference,
      sharedSurvey: message.survey ?? null,
      survey: message.survey,
      files: (message.files ?? []).map((file) => ({
        key: file.id,
        name: file.name,
        mimeType: file.mimeType,
        uri: file.uri,
      })),
      outgoingFiles: message.files,
      failureCode: message.failureCode,
    })),
  ];

  const items: ThreadItem[] = [];

  let previousDate: Date | null = null;

  for (const message of messages) {
    const date = new Date(message.createdAt);

    const hasDate = !Number.isNaN(date.getTime());

    if (hasDate && (!previousDate || !isSameDay(previousDate, date))) {
      items.push({ kind: 'day', key: `day-${message.id}`, label: dayLabel(date) });

      previousDate = date;
    }

    items.push(message);
  }

  return items;
}

/**
 * Refusals that would repeat on a resend, because the server refused the
 * inquiry or survey the message carries and a retry sends the same one. Kept
 * apart from the wording below, so naming a transient failure never hides its
 * Retry.
 */
const NON_RETRYABLE_FAILURE_CODES: ReadonlySet<string> = new Set([
  'INVALID_REFERENCE',
  'REFERENCE_NOT_ALLOWED',
  'INVALID_SURVEY',
  'SURVEY_SHARE_NOT_ALLOWED',
]);

/** Whether resending a failed message could succeed. */
export function isRetryableFailure(failureCode: string | undefined): boolean {
  return !failureCode || !NON_RETRYABLE_FAILURE_CODES.has(failureCode);
}

/**
 * Words for a failed message's refusal, when the server refused the inquiry or
 * survey it carried. Staff then know to delete it and send again without it.
 */
export function failureReasonFor(failureCode: string | undefined): string | undefined {
  switch (failureCode) {
    case 'INVALID_REFERENCE':
      return 'That inquiry can’t be attached for this member.';
    case 'REFERENCE_NOT_ALLOWED':
      return 'Only Primum Support can attach an inquiry.';
    case 'INVALID_SURVEY':
      return 'That survey isn’t published anymore.';
    case 'SURVEY_SHARE_NOT_ALLOWED':
      return 'That survey can’t be shared.';
    default:
      return undefined;
  }
}
